import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Mail, Phone, MapPin, ChevronDown, ChevronUp, Heart } from 'lucide-react';
import Home from './pages/Home';
import Admin from './pages/Admin';
import FAQ from './pages/FAQ';

function App() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-[#f4f1ea] relative before:content-[''] before:fixed before:inset-0 before:bg-noise before:opacity-50 before:pointer-events-none">
        <div className="relative z-10">
          <header className="py-10">
            <div className="container mx-auto px-4">
              <div className="flex justify-center">
                <Link to="/">
                  <img 
                    src="/Logo.png"
                    alt="Kochaj ludzi" 
                    className="h-[180px] object-contain logo-hover transition-transform duration-300"
                  />
                </Link>
              </div>
            </div>
          </header>

          <div className="h-8"></div>

          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/faq" element={<FAQ />} />
            </Routes>
          </main>

          <footer className="mt-24 relative z-20 overflow-hidden">
            <div className="absolute inset-0 bg-black opacity-95"></div>
            
            <div className="relative container mx-auto px-4 max-w-4xl pt-16 pb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
                {/* Regulamin */}
                <div className="text-center md:text-left">
                  <button
                    onClick={() => toggleSection('regulamin')}
                    className="w-full py-3 flex justify-between items-center text-white group"
                  >
                    <span className="font-['Indie_Flower'] text-2xl group-hover:text-red-400 transition-colors">
                      Regulamin
                    </span>
                    {expandedSection === 'regulamin' ? (
                      <ChevronUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-red-400" />
                    )}
                  </button>
                  <div className={`mt-4 space-y-3 transition-all duration-300 ${
                    expandedSection === 'regulamin' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                  }`}>
                    <Link to="/regulamin" className="block text-gray-400 hover:text-white transition-colors">
                      Zasady publikacji
                    </Link>
                    <Link to="/prywatnosc" className="block text-gray-400 hover:text-white transition-colors">
                      Polityka prywatności
                    </Link>
                    <Link to="/cookies" className="block text-gray-400 hover:text-white transition-colors">
                      Polityka cookies
                    </Link>
                  </div>
                </div>

                {/* FAQ */}
                <div className="text-center md:text-left">
                  <button
                    onClick={() => toggleSection('faq')}
                    className="w-full py-3 flex justify-between items-center text-white group"
                  >
                    <span className="font-['Indie_Flower'] text-2xl group-hover:text-red-400 transition-colors">
                      FAQ
                    </span>
                    {expandedSection === 'faq' ? (
                      <ChevronUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-red-400" />
                    )}
                  </button>
                  <div className={`mt-4 space-y-3 transition-all duration-300 ${
                    expandedSection === 'faq' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                  }`}>
                    <Link to="/faq#upload" className="block text-gray-400 hover:text-white transition-colors">
                      Jak dodać zdjęcie?
                    </Link>
                    <Link to="/faq#guidelines" className="block text-gray-400 hover:text-white transition-colors">
                      Wytyczne dla zdjęć
                    </Link>
                  </div>
                </div>

                {/* Kontakt */}
                <div className="text-center md:text-left">
                  <button
                    onClick={() => toggleSection('kontakt')}
                    className="w-full py-3 flex justify-between items-center text-white group"
                  >
                    <span className="font-['Indie_Flower'] text-2xl group-hover:text-red-400 transition-colors">
                      Kontakt
                    </span>
                    {expandedSection === 'kontakt' ? (
                      <ChevronUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-red-400" />
                    )}
                  </button>
                  <div className={`mt-4 space-y-3 transition-all duration-300 ${
                    expandedSection === 'kontakt' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                  }`}>
                    <a 
                      href="mailto:kontakt@kochajludzi.pl" 
                      className="flex items-center justify-center md:justify-start text-gray-400 hover:text-white transition-colors"
                    >
                      <Mail className="w-4 h-4 mr-2 text-red-400" />
                      kontakt@kochajludzi.pl
                    </a>
                    <a 
                      href="tel:+48123456789" 
                      className="flex items-center justify-center md:justify-start text-gray-400 hover:text-white transition-colors"
                    >
                      <Phone className="w-4 h-4 mr-2 text-red-400" />
                      +48 123 456 789
                    </a>
                    <div className="flex items-center justify-center md:justify-start text-gray-400">
                      <MapPin className="w-4 h-4 mr-2 text-red-400" />
                      Warszawa, Polska
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-16 pt-8 border-t border-white/10 text-center space-y-4">
                <p className="text-gray-500 font-['Indie_Flower'] text-lg">
                  © {new Date().getFullYear()} Kochaj ludzi. Wszystkie prawa zastrzeżone.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-gray-400 text-sm">Przygotowane z</span>
                  <Heart className="w-5 h-5 text-red-400 fill-red-400" />
                  <span className="text-gray-400 text-sm">przez</span>
                  <a 
                    href="https://www.kodevo.pl" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-transform hover:scale-105"
                  >
                    <img 
                      src="/KODEVO Pelne.png" 
                      alt="KODEVO" 
                      className="h-6 object-contain"
                    />
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
      <Toaster position="top-center" />
    </Router>
  );
}

export default App;